import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/api';

const EventCalendar = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [loadErrorMessage, setLoadErrorMessage] = useState('');

  useEffect(() => {
    let isMounted = true;

    const loadEvents = async () => {
      try {
        const response = await apiFetch('/api/events?limit=20&auto_import=true');
        if (!response.ok) {
          if (isMounted) {
            setUpcomingEvents([]);
            let message = 'Unable to retrieve calendar events, please try again later.';
            if (response.status === 500) {
              try {
                const errorBody = await response.clone().json();
                if (
                  typeof errorBody?.detail === 'string' &&
                  errorBody.detail.toLowerCase().includes('failed to retrieve calendar events')
                ) {
                  message = 'Internal server error, please try again later.';
                }
              } catch {
                // If body is not JSON, treat this as unavailable/offline path.
              }
            }
            setLoadErrorMessage(message);
          }
          return;
        }

        const events = await response.json();
        if (isMounted) {
          const visibleEvents = Array.isArray(events)
            ? events.filter((event) => !event.status || event.status === 'scheduled')
            : [];
          setUpcomingEvents(visibleEvents);
          setLoadErrorMessage('');
        }
      } catch {
        if (isMounted) {
          setUpcomingEvents([]);
          setLoadErrorMessage('Unable to retrieve calendar events, please try again later.');
        }
      }
    };

    loadEvents();
    return () => {
      isMounted = false;
    };
  }, []);

  // Format the date for display
  const formatEventDate = (dateStr) => {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return { day: '--', month: 'TBD', year: '' };
    }

    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return { day, month, year };
  };

  const getMapUrl = (event) => {
    if (event.mapUrl) {
      return event.mapUrl;
    }

    const query = [event.location, event.city].filter(Boolean).join(', ');
    if (!query) {
      return null;
    }

    return `https://maps.google.com/?q=${encodeURIComponent(query)}`;
  };

  const toCalendarDate = (value) => {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date.toISOString().slice(0, 10).replace(/-/g, '');
  };

  const getGoogleCalendarUrl = (event) => {
    const startDate = toCalendarDate(event.startDate || event.date);
    if (!startDate) {
      return null;
    }

    let endDate = toCalendarDate(event.endDate);
    if (!endDate) {
      const nextDay = new Date(event.startDate || event.date);
      nextDay.setDate(nextDay.getDate() + 1);
      endDate = nextDay.toISOString().slice(0, 10).replace(/-/g, '');
    }

    const location = [event.location, event.city].filter(Boolean).join(', ');
    return (
      `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(event.title || 'Storm Chasing Event')}` +
      `&dates=${startDate}/${endDate}` +
      `&location=${encodeURIComponent(location)}` +
      `&details=${encodeURIComponent(event.description || '')}`
    );
  };

  return (
    <section id="events" className="py-24 bg-gradient-to-b from-black via-gray-900 to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-red-500/10 rounded-full filter blur-[150px]" />
        <div className="absolute bottom-1/3 left-0 w-[300px] h-[300px] bg-red-500/5 rounded-full filter blur-[120px]" />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ amount: 0.2 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-2 bg-red-500/20 text-red-400 rounded-full text-sm font-medium mb-4">
            Mark Your Calendar
          </span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Upcoming Events
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Join us at these upcoming storm chasing events and meetups
          </p>
        </motion.div>

        {/* Events List */}
        {loadErrorMessage ? (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.2 }}
            className="text-center p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <Calendar size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              {loadErrorMessage}
            </p>
          </motion.div>
        ) : upcomingEvents.length > 0 ? (
          <>
            <div className="space-y-6">
              {upcomingEvents.map((event, index) => {
                const { day, month, year } = formatEventDate(event.date);
                const mapUrl = getMapUrl(event);
                const calendarUrl = getGoogleCalendarUrl(event);

                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.2 }}
                    transition={{ delay: index * 0.1 }}
                    className="group"
                  >
                    <div className="relative rounded-2xl overflow-hidden">
                      {/* Gradient border */}
                      <div className="absolute inset-0 bg-gradient-to-br from-red-500/40 via-gray-800 to-gray-800 rounded-2xl" />

                      {/* Card content */}
                      <div className="relative m-[1px] bg-gray-900/95 rounded-2xl overflow-hidden">
                        <div className="flex flex-col md:flex-row">
                          {/* Date Block */}
                          <div className="md:w-48 flex-shrink-0 bg-gradient-to-br from-red-600 to-red-700 p-6 flex flex-col items-center justify-center text-center">
                            <span className="text-white/80 text-sm font-medium tracking-wider">{month}</span>
                            <span className="text-white text-6xl font-bold leading-none my-2">{day}</span>
                            <span className="text-white/80 text-lg font-medium">{year}</span>
                          </div>

                          {/* Event Details */}
                          <div className="flex-1 p-6 md:p-8">
                            <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                              <div>
                                <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
                                  {event.title}
                                </h3>
                                {event.isMultiGroup && (
                                  <span className="inline-flex items-center gap-1 px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium">
                                    <Users size={14} />
                                    Multiple Groups Attending
                                  </span>
                                )}
                              </div>
                            </div>

                            <p className="text-gray-300 text-lg mb-6 leading-relaxed">
                              {event.description}
                            </p>

                            <div className="flex flex-wrap gap-6 text-gray-400">
                              {event.time && (
                                <div className="flex items-center gap-2">
                                  <Clock size={18} className="text-red-400" />
                                  <span>{event.time}</span>
                                </div>
                              )}
                              {(event.location || event.city) && (
                                <div className="flex items-center gap-2">
                                  <MapPin size={18} className="text-red-400" />
                                  <span>{[event.location, event.city].filter(Boolean).join(', ')}</span>
                                </div>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-wrap gap-4 mt-6 pt-6 border-t border-gray-800">
                              {mapUrl && (
                                <a
                                  href={mapUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-white font-medium rounded-full transition-colors duration-200"
                                >
                                  <MapPin size={16} />
                                  View on Map
                                  <ExternalLink size={14} />
                                </a>
                              )}
                              <button
                                onClick={() => {
                                  if (!calendarUrl) {
                                    return;
                                  }
                                  window.open(calendarUrl, '_blank');
                                }}
                                disabled={!calendarUrl}
                                className={`inline-flex items-center gap-2 px-5 py-2.5 text-white font-medium rounded-full transition-colors duration-200 ${
                                  calendarUrl
                                    ? 'bg-red-600 hover:bg-red-500'
                                    : 'bg-gray-700 cursor-not-allowed opacity-60'
                                }`}
                              >
                                <Calendar size={16} />
                                Add to Calendar
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ amount: 0.2 }}
              className="text-center mt-12 p-8 rounded-2xl bg-gray-900/50 border border-gray-800"
            >
              <Calendar size={40} className="text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">
                More events coming soon! Follow us on Facebook for the latest updates.
              </p>
            </motion.div>
          </>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ amount: 0.2 }}
            className="text-center p-10 rounded-2xl bg-gray-900/50 border border-gray-800"
          >
            <Calendar size={40} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              No upcoming events are currently listed.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default EventCalendar;
